import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import ReassuranceBar from '@/components/feature/ReassuranceBar';
import CTABanner from '@/components/feature/CTABanner';
import SEOHead from '@/components/feature/SEOHead';
import { useState } from 'react';

const faqData = [
  {
    q: "Pourquoi mon double vitrage est-il embué de l'intérieur ?",
    a: "La buée entre les deux verres signifie que le joint d'étanchéité périphérique du vitrage est défectueux. L'humidité s'infiltre dans la lame d'air et se condense sur les faces intérieures du verre. Le vitrage a perdu son pouvoir isolant et doit être remplacé.",
  },
  {
    q: "Peut-on réparer un double vitrage embué sans le changer ?",
    a: "Non, il n'existe pas de solution durable pour réparer un double vitrage embué. Des kits de « désembuage » existent mais leurs résultats sont temporaires et peu fiables. Le seul traitement efficace est le remplacement complet du vitrage.",
  },
  {
    q: "Un double vitrage embué augmente-t-il ma facture de chauffage ?",
    a: "Oui, un vitrage embué a perdu une grande partie de ses propriétés isolantes. L'humidité à l'intérieur de la lame d'air annule l'efficacité thermique du double vitrage. Le remplacer permet de retrouver une isolation optimale.",
  },
];

export default function DoubleVitrageEmbue() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const serviceJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Double vitrage embué à Lyon : remplacement',
    provider: { '@type': 'HomeAndConstructionBusiness', name: 'DFPRO Lyon', telephone: '+33980803684' },
    areaServed: [{ '@type': 'City', name: 'Lyon' }, { '@type': 'AdministrativeArea', name: 'Métropole de Lyon' }],
    description: 'Double vitrage embué à Lyon ? Joint HS, condensation entre les verres. Remplacement rapide du vitrage. Devis gratuit, garantie 10 ans.',
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map((item) => ({ '@type': 'Question', name: item.q, acceptedAnswer: { '@type': 'Answer', text: item.a } })),
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Accueil', item: 'https://depanfenetres-lyon.fr/' },
      { '@type': 'ListItem', position: 2, name: 'Vitrier à Lyon', item: 'https://depanfenetres-lyon.fr/vitrier-lyon/' },
      { '@type': 'ListItem', position: 3, name: 'Double vitrage embué', item: 'https://depanfenetres-lyon.fr/double-vitrage-embue-lyon/' },
    ],
  };

  return (
    <>
      <SEOHead
        title="Double vitrage embué à Lyon : que faire ? | DFPRO"
        description="Double vitrage embué entre les verres ? Joint HS, isolation perdue. Remplacement vitrage rapide à Lyon. Devis gratuit, garantie 10 ans. DFPRO Lyon."
        canonicalUrl="https://depanfenetres-lyon.fr/double-vitrage-embue-lyon/"
        ogTitle="Double vitrage embué à Lyon — Remplacement rapide | DFPRO"
        ogDescription="Condensation entre les verres ? Votre vitrage est HS. Remplacement à Lyon, devis gratuit, garantie 10 ans."
        jsonLd={{ ...serviceJsonLd, ...faqJsonLd, ...breadcrumbJsonLd }}
      />

      <Header />

      <main>
        <div className="pt-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
            <nav className="flex items-center gap-2 text-xs text-secondary-400">
              <a href="/" className="hover:text-primary-500 transition-colors">Accueil</a>
              <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center"></i>
              <a href="/vitrier-lyon/" className="hover:text-primary-500 transition-colors">Vitrier à Lyon</a>
              <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center"></i>
              <span className="text-foreground-700 font-medium">Double vitrage embué</span>
            </nav>
          </div>
        </div>

        <section className="relative w-full min-h-[380px] md:min-h-[420px] flex items-center bg-background-50">
          <div className="absolute inset-0">
            <img
              src="/images/vitrage-embue-hero-2026.jpg"
              alt="Double vitrage embué à Lyon — condensation entre les verres, remplacement par DFPRO Lyon"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-foreground-900/50 via-foreground-900/35 to-foreground-900/50"></div>
          </div>
          <div className="relative z-10 w-full px-4 md:px-6 py-14 md:py-16">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="font-heading font-bold text-2xl md:text-4xl text-background-50 leading-tight">
                Double vitrage embué à Lyon
              </h1>
              <p className="mt-4 md:mt-5 text-sm md:text-lg text-background-50/90 max-w-2xl mx-auto leading-relaxed">
                De la condensation s'est formée entre les deux vitres de votre fenêtre ? Votre double vitrage est défectueux. Nos vitriers le remplacent rapidement à Lyon.
              </p>
              <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
                <a href="tel:+33980803684" className="inline-flex items-center gap-2 bg-accent-500 hover:bg-accent-600 text-background-50 font-semibold text-sm md:text-base px-6 py-3.5 rounded-md transition-colors whitespace-nowrap cursor-pointer shadow-[0_2px_8px_rgba(242,140,40,0.4)]">
                  <i className="ri-phone-fill text-lg w-5 h-5 flex items-center justify-center"></i>
                  Appeler — 09 80 80 36 84
                </a>
              </div>
            </div>
          </div>
        </section>

        <ReassuranceBar />

        <section className="w-full bg-background-50 py-14 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-12">
              <div className="lg:col-span-2">
                <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-800 mb-5">
                  Pourquoi mon double vitrage est-il embué ? Comprendre et agir
                </h2>
                <div className="space-y-4 text-sm md:text-base text-secondary-500 leading-relaxed">
                  <p>
                    Vous regardez par votre fenêtre et une buée opaque gêne la vue, non pas sur la face intérieure (ce qui serait normal par temps humide), mais <strong className="text-foreground-800">entre les deux vitres</strong>. Ce phénomène est le signe incontestable que le joint d'étanchéité périmétrique de votre double vitrage a cédé. L'humidité de l'air s'est infiltrée dans la lame d'air entre les deux verres et vient s'y condenser.
                  </p>
                  <p>
                    Cette situation est irréversible. Le joint périphérique d'un double vitrage est composé d'un <strong className="text-foreground-800">double système d'étanchéité</strong> : un joint butyle intérieur (contre l'humidité) et un joint silicone ou polysulfure extérieur (tenue mécanique). Avec le temps, l'exposition aux UV, aux variations de température et aux mouvements de la menuiserie, ces joints peuvent se dégrader et laisser passer l'humidité.
                  </p>
                  <p>
                    Un double vitrage embué n'est pas qu'un problème esthétique. La présence d'humidité dans la lame d'air <strong className="text-foreground-800">annule les propriétés isolantes du vitrage</strong>. Votre fenêtre laisse passer le froid en hiver et la chaleur en été, ce qui impacte directement votre confort et votre facture énergétique. De plus, cette humidité persistante peut, à terme, provoquer des taches blanchâtres définitives sur la face interne des verres.
                  </p>
                  <p>
                    La seule solution durable est le <strong className="text-foreground-800">remplacement complet du vitrage</strong>. Chez DFPRO Lyon, nous intervenons rapidement à Lyon et dans la Métropole de Lyon : nous prenons les mesures précises, commandons un vitrage neuf aux dimensions exactes, et procédons à la pose. Nous remplaçons le double vitrage par un modèle récent, souvent un <strong className="text-foreground-800">vitrage isolant renforcé (VIR)</strong> à isolation thermique améliorée, ce qui vous fait bénéficier des progrès technologiques des dernières années.
                  </p>
                  <p>
                    Le processus d'intervention est rapide et propre : notre vitrier dépose les parcloses, retire l'ancien vitrage embué, nettoie la feuillure, installe le nouveau vitrage avec des cales de positionnement adaptées, puis repose les parcloses. L'intervention dure généralement <strong className="text-foreground-800">moins d'une heure par fenêtre</strong> et ne génère quasiment pas de poussière. Comme toutes nos prestations, le remplacement bénéficie de notre <strong className="text-foreground-800">garantie 10 ans</strong>.
                  </p>
                  <p className="bg-background-100 p-4 rounded-md text-foreground-700">
                    <strong>À savoir :</strong> ne confondez pas la condensation sur la face intérieure du vitrage (côté pièce, normale en hiver, se règle par une meilleure ventilation) avec la condensation entre les deux verres (anormale, nécessite un remplacement). Si vous avez un doute, nos techniciens peuvent vous aider à faire le diagnostic par téléphone au 09 80 80 36 84.
                  </p>
                </div>
              </div>

              <div className="bg-background-100 rounded-lg p-5 md:p-6 h-fit">
                <h3 className="font-heading font-semibold text-base text-foreground-800 mb-4">Nos services vitrier</h3>
                <nav className="space-y-2">
                  {[
                    { label: 'Vitrier à Lyon (pilier)', href: '/vitrier-lyon/' },
                    { label: 'Remplacement vitre cassée', href: '/remplacement-vitre-cassee-lyon/' },
                    { label: 'Remplacement double vitrage', href: '/remplacement-double-vitrage-lyon/' },
                    { label: 'Survitrage', href: '/survitrage-lyon/' },
                    { label: 'Vitrier urgence', href: '/vitrier-urgence-lyon/' },
                  ].map((link, idx) => (
                    <a key={idx} href={link.href} className="flex items-center gap-2 text-sm text-secondary-500 hover:text-primary-500 transition-colors py-1.5 cursor-pointer">
                      <i className="ri-arrow-right-s-line text-primary-400 w-4 h-4 flex items-center justify-center"></i>
                      {link.label}
                    </a>
                  ))}
                </nav>
                <div className="mt-5 pt-5 border-t border-background-200">
                  <a href="tel:+33980803684" className="inline-flex items-center justify-center gap-2 w-full bg-accent-500 hover:bg-accent-600 text-background-50 font-semibold text-sm py-3 rounded-md transition-colors cursor-pointer">
                    <i className="ri-phone-fill w-4 h-4 flex items-center justify-center"></i>
                    Appeler — 09 80 80 36 84
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100 py-14 md:py-20">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="font-heading font-bold text-xl md:text-3xl text-foreground-800">Questions fréquentes</h2>
            </div>
            <div className="space-y-3">
              {faqData.map((item, idx) => (
                <div key={idx} className="bg-background-50 rounded-lg border border-background-200 overflow-hidden">
                  <button onClick={() => setOpenFaq(openFaq === idx ? null : idx)} className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer">
                    <span className="font-heading font-semibold text-sm md:text-base text-foreground-800 pr-4">{item.q}</span>
                    <span className={`flex-shrink-0 transition-transform duration-200 ${openFaq === idx ? 'rotate-180' : ''}`}>
                      <i className="ri-arrow-down-s-line text-secondary-400 w-5 h-5 flex items-center justify-center"></i>
                    </span>
                  </button>
                  {openFaq === idx && (
                    <div className="px-5 pb-4"><p className="text-sm text-secondary-500 leading-relaxed">{item.a}</p></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        <CTABanner
          title="Double vitrage embué à Lyon ?"
          subtitle="Ne perdez plus d'énergie ! Appelez le 09 80 80 36 84, remplacement rapide du vitrage."
          buttonText="Appeler maintenant"
          secondaryButtonText="Demander un devis"
          secondaryButtonHref="/contact/"
        />
      </main>
      <Footer />
    </>
  );
}